import React from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// components
import { SbSharedLink } from '../SbSharedLink/SbSharedLink';
import { SbSharedCategoryTilesTracking } from '../SbSharedCategoryTilesTracking/SbSharedCategoryTilesTracking';
import { SbRespTilesSwiper } from '../SbRespTilesSwiper/SbRespTilesSwiper';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';

// utils
import { getSrcsetString } from '../../utils/srcSet';
import { createNewUriRef, getQueryParam } from '../../utils/uriUtils';

// styles
import styles from './sbRespCategoryTiles.scss';

//constants
import { SRC_SET_SIZES_TILE } from '../../constants/imageConstants';
import { SORT } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants';

export const SbRespCategoryTilesComponent = props => {
    const { itemSearch } = props;
    const { displayUriRef } = itemSearch;
    if (!get(itemSearch, 'categoryTiles.length')) {
        return null;
    }

    /**
     * We show 5 tiles max.
     *
     * Special case - for fashion we only show 4, but that is already handled in graphql, so we
     * don't need to handle it here.
     *
     */
    const tiles = itemSearch.categoryTiles.slice(0, 5);
    const srcSets = tiles.map(tile => getSrcsetString(tile.masterImage));
    const sort = getQueryParam(displayUriRef, SORT);

    return (
        <ServerVarsContextConsumer>
            {({ isTablet }) => {
                const containerClass = classnames(styles.container, {
                    rowFlex: !isTablet,
                });
                return (
                    <SbSharedCategoryTilesTracking itemSearch={itemSearch} maxTilesAllowed={5}>
                        {({ trackClick }) => (
                            <div className={`${containerClass}`} data-tn="category-tiles">
                                <SbRespTilesSwiper
                                    displayUriRef={displayUriRef}
                                    isTablet={isTablet}
                                >
                                    {tiles.map((categoryTile, index) => {
                                        const { displayName, url, image } = categoryTile;
                                        // to persist the sort query in the category tile link
                                        const href = sort ? createNewUriRef(url, { sort }) : url;

                                        return (
                                            <SbSharedLink
                                                className={`${styles.itemContainer} colXs3`}
                                                href={href}
                                                onClick={() => trackClick(index)}
                                                key={displayName}
                                            >
                                                <div className={styles.imageWrapper}>
                                                    <img
                                                        className={styles.image}
                                                        src={image}
                                                        alt={displayName}
                                                        srcSet={srcSets[index]}
                                                        sizes={SRC_SET_SIZES_TILE}
                                                    />
                                                </div>
                                                <div className={styles.title}>{displayName}</div>
                                            </SbSharedLink>
                                        );
                                    })}
                                </SbRespTilesSwiper>
                            </div>
                        )}
                    </SbSharedCategoryTilesTracking>
                );
            }}
        </ServerVarsContextConsumer>
    );
};

SbRespCategoryTilesComponent.propTypes = {
    itemSearch: PropTypes.object.isRequired,
};

export const SbRespCategoryTiles = createFragmentContainer(SbRespCategoryTilesComponent, {
    itemSearch: graphql`
        fragment SbRespCategoryTiles_itemSearch on ItemSearchQueryConnection {
            ...SbSharedCategoryTilesTracking_itemSearch
            displayUriRef
            categoryTiles {
                displayName
                rank
                url
                image
                masterImage
            }
        }
    `,
});
